import { EntityNode } from "./EntityNode";
import { RotationNode } from "../Nodes/RotationNode";
import { ResizeNode } from "../Nodes/ResizeNode";
import { ActionStateEnum } from "../../state/actionState/ActionState";
import { DimensionNode } from "../Nodes/DimensionNode";
import { GameObjectManager } from "../GameObjectManager";
import { Utils } from "../../utils/Utils";
import { PlanManager } from "../../../../cocosPlan";
import { Events, eventsManager, GeometryPrimitive, math, SceneConstants } from "@rhinov/savane-js";

declare var cc: any;

export class GeometryPrimitiveNode extends EntityNode {
    
    _NOT_VALID_OVERLAP_COLOR = new cc.Color(255, 125, 141, 255);

    constructor(geomPrim: GeometryPrimitive) {
        super(geomPrim);
        this.isValid = true;
        this.geomPrim = geomPrim;

        if (this.geomPrim.primitiveType !== SceneConstants.GeometryPrimitiveType.cylinder && this.geomPrim.primitiveType !== SceneConstants.GeometryPrimitiveType.sphere) {
            this.rotationmenu = new RotationNode(geomPrim);
            this.addChild(this.rotationmenu);
        }

        this.resizeMenu = new ResizeNode(geomPrim, false, 1);
        this.addChild(this.resizeMenu, 8);

        this.createDimensions();

        this.hideGeometryPrimitivetAxo = eventsManager.instance.addListener(Events.HIDE_AXO, (_event: any) => {
            this.needRedraw = true;
            if (this.geomPrim.hideInAxo) {
                this.setVisible(!PlanManager.getInstance()._hideAxo);
            }
        });
    }

    updateDimensionNode(node: any, begin: any, end: any, normal: any, dist: number) {
        var cote_begin = new cc.p(begin.x + dist * normal.x, begin.y + dist * normal.y);
        var cote_end = new cc.p(end.x + dist * normal.x, end.y + dist * normal.y);
        var cote_pos = new cc.p((cote_begin.x + cote_end.x) / 2, (cote_begin.y + cote_end.y) / 2);
        node.setPosition(cote_pos);
        var calcDist = Math.sqrt((end.x - begin.x) * (end.x - begin.x) + (end.y - begin.y) * (end.y - begin.y));
        node.setDimensionValue(calcDist, PlanManager.getInstance().getZoomScale());

        var angle = Math.atan2(end.x - begin.x, end.y - begin.y);
        var cocosAngle = (angle + Math.PI * 0.5) * 180 / Math.PI;
        if (cocosAngle < -90.0)
            cocosAngle += 180;
        if (cocosAngle >= 90.0)
            cocosAngle -= 180;

        var parentAngle = (this.geomPrim.angle * 180) / Math.PI;
        if (parentAngle < -90.0)
            cocosAngle += 180;
        if (parentAngle > 90.0)
            cocosAngle -= 180;

        node.setRotation(cocosAngle);
    }

    updateDimensions() {
        var norm1 = new cc.p(0, -1);
        var norm2 = new cc.p(1, 0);
        var width;
        var length;

        if (this.geomPrim.primitiveType === SceneConstants.GeometryPrimitiveType.box || this.geomPrim.primitiveType === SceneConstants.GeometryPrimitiveType.triangle || this.geomPrim.primitiveType === SceneConstants.GeometryPrimitiveType.oval || this.geomPrim.primitiveType === SceneConstants.GeometryPrimitiveType.halfarc) {
            width = this.geomPrim.width;
            length = this.geomPrim.length;
        }

        if (this.geomPrim.primitiveType === SceneConstants.GeometryPrimitiveType.hood) {
            width = this.geomPrim.width;
            if (this.geomPrim.topWidth > width) {
                width = this.geomPrim.topWidth;
            }
            length = this.geomPrim.length;
            if (this.geomPrim.topLength > length) {
                length = this.geomPrim.topLength;
            }
        }

        if (this.geomPrim.primitiveType === SceneConstants.GeometryPrimitiveType.cylinder || this.geomPrim.primitiveType === SceneConstants.GeometryPrimitiveType.sphere) {
            width = this.geomPrim.diameter;
            length = this.geomPrim.diameter;
        }

        this.updateDimensionNode(this.dim1, new cc.p((length / 2) / 10, -(width / 2) / 10), new cc.p(-(length / 2) / 10, -(width / 2) / 10), norm1, Utils.DIMENSION_DIST * 1.5);
        this.updateDimensionNode(this.dim2, new cc.p((length / 2) / 10, -(width / 2) / 10), new cc.p((length / 2) / 10, (width / 2) / 10), norm2, Utils.DIMENSION_DIST * 1.5);
    }

    createDimensions() {
        this.dim1 = new DimensionNode(undefined);
        this.dim1.setName('geomPrimDimension1');
        this.dim1.setTag(this.geomPrim.id);
        this.dim1.setVisible(false);
        this.addChild(this.dim1, 8);

        this.dim2 = new DimensionNode(undefined);
        this.dim1.setName('geomPrimDimension2');
        this.dim2.setTag(this.geomPrim.id);
        this.dim2.setVisible(false);
        this.addChild(this.dim2, 8);
    }

    toRotate(position: math.vec3): boolean {
        if (this.rotationmenu) {
            return this.rotationmenu.toRotate(position);
        }
        return (false);
    }

    getOverLapColor(isValid: boolean): {border:any, plain: any} {
        var colorItem = null;
        if (isValid) {
            if (this._isSelected) {
                colorItem = {
                    plain: new cc.color('#fbe800'),
                    border: new cc.color('#fbe800')
                };
            }
            else {
                if (this._isHovered) {
                    colorItem = {
                        plain: new cc.color(185, 185, 185),
                        border: new cc.Color(66, 80, 94, 255)
                    };
                } else {
                    colorItem = {
                        plain: new cc.color(255, 255, 255),
                        border: new cc.Color(66, 80, 94, 255)
                    };
                }
            }
        } else {
            colorItem = {
                plain: this._NOT_VALID_OVERLAP_COLOR,
                border: this._NOT_VALID_OVERLAP_COLOR
            };
        }
        if (PlanManager.getInstance().highlightMode) {
            colorItem.plain.a = 75;
        }

        if ((this.geomPrim.floor !== null) && (this.geomPrim.floor.id !== PlanManager.getInstance().world.currentScene!.currentFloor!.id)) {
            colorItem.plain.a = PlanManager.getInstance().opacityFloors;
            colorItem.border.a = PlanManager.getInstance().opacityFloors;
        }

        return colorItem;
    }

    updateModel(isValid: boolean) {
        var planManager = PlanManager.getInstance();

        this.clear();
        if (isValid != undefined) {
            this.isValid = isValid;
        }

        // In decoration state, always hide rotation and resize nodes
        if (planManager.getAction() !== ActionStateEnum.idleDecoration) {
            if (this.rotationmenu) {
                this.rotationmenu.setVisible(this._isSelected);
                this.rotationmenu.update();
            }
            this.resizeMenu.setVisible(this._isSelected);
            this.resizeMenu.update();
        }
        else {
            if (this.rotationmenu) {
                this.rotationmenu.setVisible(false);
            }
            this.resizeMenu.setVisible(false);
        }

        this.updateDimensions();

        this.dim1.setVisible(this._isSelected);
        this.dim1.update();
        this.dim2.setVisible(this._isSelected)
        this.dim2.update();

        //  var topleft = new cc.p(0,0);
        //  var botRight = new cc.p(50,50);
        //var color = this.isValid ? (this._isSelected ? new cc.color(190, 190, 190) : new cc.color(155, 155, 155)) : new cc.color(255, 125, 141, 255);

        var color: {border: any, plain: any} = {border: undefined, plain: undefined};
        if (isValid != undefined) {
            color = this.getOverLapColor(isValid);
        }
        else {
            color = this.getOverLapColor(true);
        }

        var yellow = new cc.color('F5D231');
        if ((this.geomPrim.floor !== null) && (this.geomPrim.floor.id !== PlanManager.getInstance().world.currentScene!.currentFloor!.id)) {
            yellow.a = PlanManager.getInstance().opacityFloors;
        }

        var greyColor = new cc.Color(66, 80, 94, 255);

        switch (this.geomPrim.primitiveType) {
            case SceneConstants.GeometryPrimitiveType.box:
                var topLeft = new cc.p(-this.geomPrim.length / 20, -this.geomPrim.width / 20);
                var botRight = new cc.p(this.geomPrim.length / 20, this.geomPrim.width / 20);
                var topRight = new cc.p(-this.geomPrim.length / 20, this.geomPrim.width / 20);
                var botLeft = new cc.p(this.geomPrim.length / 20, -this.geomPrim.width / 20);

                this.drawRect(topLeft, botRight, color.plain, 2, color.border);
                this.drawSegment(topLeft, botRight, 2, greyColor);
                this.drawSegment(topRight, botLeft, 2, greyColor);
                break;

            case SceneConstants.GeometryPrimitiveType.cylinder:
                var radius = this.geomPrim.diameter / 20;
                var topLeft = new cc.p(radius * Math.cos(135 * Math.PI / 180), radius * Math.sin(135 * Math.PI / 180));
                var botRight = new cc.p(radius * Math.cos(315 * Math.PI / 180), radius * Math.sin(315 * Math.PI / 180));
                var topRight = new cc.p(radius * Math.cos(45 * Math.PI / 180), radius * Math.sin(45 * Math.PI / 180));
                var botLeft = new cc.p(radius * Math.cos(225 * Math.PI / 180), radius * Math.sin(225 * Math.PI / 180));

                this.drawDot(cc.p(0, 0), radius, color.border);
                if (radius - 2 > 0) {
                    this.drawDot(cc.p(0, 0), radius - 2, color.plain);
                }
                this.drawSegment(topLeft, botRight, 2, greyColor);
                this.drawSegment(topRight, botLeft, 2, greyColor);
                break;

            case SceneConstants.GeometryPrimitiveType.triangle:
                var topLeft = new cc.p(-this.geomPrim.length / 20, this.geomPrim.width / 20);
                var topRight = new cc.p(this.geomPrim.length / 20, -this.geomPrim.width / 20);
                var botRight = new cc.p(this.geomPrim.length / 20, this.geomPrim.width / 20);

                this.drawSegment(topLeft, topRight, 2, color.border);
                this.drawSegment(topRight, botRight, 2, color.border);
                this.drawSegment(botRight, topLeft, 2, color.border);
                break;

            case SceneConstants.GeometryPrimitiveType.oval:
                var nbSegment = 6;
                for (var i = 0; i < nbSegment * 4; i++) {
                    var pt1 = new cc.p((this.geomPrim.length / 20) * Math.cos(i * (Math.PI * 2) / (nbSegment * 4)), (this.geomPrim.width / 20) * Math.sin(i * (Math.PI * 2) / (nbSegment * 4)));
                    var pt2 = new cc.p((this.geomPrim.length / 20) * Math.cos((i + 1) * (Math.PI * 2) / (nbSegment * 4)), (this.geomPrim.width / 20) * Math.sin((i + 1) * (Math.PI * 2) / (nbSegment * 4)));
                    this.drawSegment(pt1, pt2, 2, color.border);
                }
                break;

            case SceneConstants.GeometryPrimitiveType.sphere:
                var radius = this.geomPrim.diameter / 20;
                var topLeft = new cc.p(radius * Math.cos(135 * Math.PI / 180), radius * Math.sin(135 * Math.PI / 180));
                var botRight = new cc.p(radius * Math.cos(315 * Math.PI / 180), radius * Math.sin(315 * Math.PI / 180));
                var topRight = new cc.p(radius * Math.cos(45 * Math.PI / 180), radius * Math.sin(45 * Math.PI / 180));
                var botLeft = new cc.p(radius * Math.cos(225 * Math.PI / 180), radius * Math.sin(225 * Math.PI / 180));

                this.drawDot(cc.p(0, 0), radius, color.border);
                if (radius - 2 > 0) {
                    this.drawDot(cc.p(0, 0), radius - 2, color.plain);
                }
                if (radius / 2 > 0) {
                    this.drawDot(cc.p(0, 0), radius / 2, greyColor);
                }
                if ((radius / 2) - 2 > 0) {
                    this.drawDot(cc.p(0, 0), (radius / 2) - 2, color.plain);
                }
                this.drawSegment(topLeft, botRight, 2, greyColor);
                this.drawSegment(topRight, botLeft, 2, greyColor);
                break;

            case SceneConstants.GeometryPrimitiveType.hood:
                var topLeft = new cc.p(-this.geomPrim.length / 20, -this.geomPrim.width / 20);
                var botRight = new cc.p(this.geomPrim.length / 20, this.geomPrim.width / 20);

                this.drawRect(topLeft, botRight, color.plain, 2, color.border);

                var topTopLeft = new cc.p(-this.geomPrim.topLength / 20, -this.geomPrim.topWidth / 20);
                var topBotRight = new cc.p(this.geomPrim.topLength / 20, this.geomPrim.topWidth / 20);

                switch(this.geomPrim.shapeType) {
                    case 0:
                        break;
                    case 1:
                        topTopLeft = new cc.p(this.geomPrim.length / 20 - this.geomPrim.topLength / 10, -this.geomPrim.topWidth / 20);
                        topBotRight = new cc.p(this.geomPrim.length / 20, this.geomPrim.topWidth / 20);
                        break;
                    case 2:
                        topTopLeft = new cc.p(-this.geomPrim.length / 20, -this.geomPrim.topWidth / 20);
                        topBotRight = new cc.p(-this.geomPrim.length / 20 + this.geomPrim.topLength / 10, this.geomPrim.topWidth / 20);
                        break;
                    case 3:
                        topTopLeft = new cc.p(-this.geomPrim.topLength / 20, -this.geomPrim.width / 20);
                        topBotRight = new cc.p(this.geomPrim.topLength / 20, -this.geomPrim.width / 20 + this.geomPrim.topWidth / 10);
                        break;
                    case 5:
                        topTopLeft = new cc.p(-this.geomPrim.length / 20, -this.geomPrim.width / 20);
                        topBotRight = new cc.p(-this.geomPrim.length / 20 + this.geomPrim.topLength / 10, -this.geomPrim.width / 20 + this.geomPrim.topWidth / 10);
                        break;
                    case 4:
                        topTopLeft = new cc.p(this.geomPrim.length / 20 - this.geomPrim.topLength / 10, -this.geomPrim.width / 20);
                        topBotRight = new cc.p(this.geomPrim.length / 20, -this.geomPrim.width / 20 + this.geomPrim.topWidth / 10);
                        break;
                }

                this.drawRect(topTopLeft, topBotRight, color.plain, 2, greyColor);
                break;

            case SceneConstants.GeometryPrimitiveType.halfarc:
                var topLeft = new cc.p(-this.geomPrim.length / 20, -this.geomPrim.width / 20);
                var botRight = new cc.p(this.geomPrim.length / 20, this.geomPrim.width / 20);
                var topRight = new cc.p(-this.geomPrim.length / 20, this.geomPrim.width / 20);
                var botLeft = new cc.p(this.geomPrim.length / 20, -this.geomPrim.width / 20);

                var pt1 = new cc.p(this.geomPrim.length / 20, 0);
                var pt2 = new cc.p(0, 0);

                var pt3 = new cc.p(-this.geomPrim.length / 40, 0);
                var pt4 = new cc.p(-this.geomPrim.length / 30, 0);

                this.drawRect(topLeft, botRight, color.plain, 2, color.border);
                this.drawSegment(pt1, pt2, 2, greyColor);
                this.drawSegment(pt3, pt4, 2, greyColor);
                break;
        }
    }

    dispose() {
    }

    setValidity(value: boolean) {
        this.isValid = value;
    }

    update(dt: any) {
        super.update(dt);
        this.setPosition(GameObjectManager.convertToCocosCoord(this.geomPrim.transform.globalPosition));
        this.setRotationZ(this.geomPrim.transform.globalZRotation);
        this.updateModel(this.geomPrim.isValid(PlanManager.getInstance().world.currentScene!.currentFloor));
    }

    toResize(pos: math.vec3) {
        return this.resizeMenu.toResize(pos);
    }

    setRotationZ(value: number) {
        this.setRotation(-(value * 360) / (Math.PI * 2.0));
    }

    removeListeners() {
        eventsManager.instance.removeListener(this.hideGeometryPrimitivetAxo);
    }

    onExit() {
        super.onExit();
        this.removeListeners();
    }
}
